import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Checkbox,
  FormControlLabel, FormHelperText, Select, MenuItem, FormControl, InputLabel, Alert,
  CircularProgress, Typography, Box,
} from '@mui/material';
import FlexService from '../../services/FlexService';
import { enterAdvanceProps } from '../../utils/enterAdvance';
import PermissionService from '../../services/PermissionService';

/**
 * "Create it right here" (FH-195): lets a user create a missing record -- a supplier on a
 * receipt, a customer on an order -- from the field that needs it, without leaving the form.
 *
 * A form field opts in from its view JSON entry, next to visibleScript:
 *
 *   {"name": "receiptSupplier", "createOption": {
 *      "entity": "Supplier",          // field definitions for the small form come from this entity
 *      "script": "supplier_create",   // FLEX Command that creates it; also the permission checked
 *      "noun": "supplier",            // "+ Create supplier 'ACME'"
 *      "valueField": "supplierId",    // the new record's value that goes back into the field
 *      "fields": ["supplierId", "supplierName", "contactName"],   // what the small form asks for
 *      "defaults": {"isActive": true} // sent but not shown
 *   }}
 */

/** {fieldName: createOption} for a form's config field list (plain names have none). */
export const createOptionsByField = (entries = []) => Object.fromEntries(
  entries
    .filter((e) => e && typeof e === 'object' && e.name && e.createOption)
    .map((e) => [e.name, e.createOption])
);

// Whether the signed-in user may run each option's create script, cached for the session
// page: the same check the server makes when the script runs, so the choice never shows
// to someone it would then refuse.
const permissionCache = {};
export const canCreateWith = (option) => {
  if (!option?.script) return Promise.resolve(false);
  if (!permissionCache[option.script]) {
    permissionCache[option.script] = PermissionService.canAccess('FLEX_SCRIPT', option.script);
  }
  return permissionCache[option.script];
};

/** Marker for the "+ Create ..." entry appended to an autocomplete's options. */
export const isCreateEntry = (opt) => !!(opt && typeof opt === 'object' && opt.__createNew);

/** Builds the "+ Create ..." entry for the text the user typed (may be blank). */
export const createEntry = (option, typed) => {
  const text = (typed || '').trim();
  return {
    __createNew: true,
    value: text,
    label: text ? `+ Create ${option.noun || 'new'} "${text}"` : `+ Create a new ${option.noun || 'record'}`,
  };
};

const InlineCreateDialog = ({ open, option, initialValue, colors = {}, onCancel, onCreated }) => {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !option?.entity) return;
    let cancelled = false;
    setError(null);
    setLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('waide_access_token');
        const res = await fetch(`/api/v1/catalog/${option.entity}/fields`, { headers: { Authorization: `Bearer ${token}` } });
        const data = res.ok ? await res.json() : [];
        const all = data.fields || data || [];
        const wanted = (option.fields && option.fields.length)
          ? option.fields.map((n) => all.find((f) => f.fieldName === n)).filter(Boolean)
          : all.filter((f) => f.isRequired && f.isVisibleInForm !== false);
        if (cancelled) return;
        setFields(wanted);
        const seed = {};
        wanted.forEach((f) => { seed[f.fieldName] = ''; });
        if (option.valueField) {
          const typed = initialValue || '';
          const vf = wanted.find((f) => f.fieldName === option.valueField);
          seed[option.valueField] = vf?.textTransform === 'UPPERCASE' ? typed.toUpperCase() : typed;
        }
        setValues(seed);
      } catch (e) {
        if (!cancelled) setError(`Couldn't open the ${option.noun || 'new record'} form. Close it and try again.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, option, initialValue]);

  const setValue = (f, v) => {
    const out = f.textTransform === 'UPPERCASE' && typeof v === 'string' ? v.toUpperCase() : v;
    setValues((prev) => ({ ...prev, [f.fieldName]: out }));
  };

  const missing = fields.filter((f) => f.isRequired && (values[f.fieldName] === '' || values[f.fieldName] == null));

  const save = async () => {
    if (missing.length > 0) {
      setError(`Fill in ${missing.map((f) => f.displayLabel).join(', ')}.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = { ...(option.defaults || {}) };
      fields.forEach((f) => {
        const v = values[f.fieldName];
        if (v === '' || v == null) return;
        payload[f.fieldName] = f.fieldType === 'NUMBER' ? Number(v) : v;
      });
      const resp = await FlexService.executeScript({ scriptName: option.script, context: { _payload: payload } });
      const ctx = resp?.context || {};
      if (!resp?.success || ctx.success === false) {
        setError(ctx.error_message || resp?.diagnostics?.[0]?.message || `The ${option.noun || 'record'} wasn't created.`);
        return;
      }
      onCreated(payload[option.valueField], payload);
    } catch (e) {
      setError(e.message || `The ${option.noun || 'record'} wasn't created.`);
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = { mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 0 } };
  const renderInput = (f) => {
    const label = f.displayLabel + (f.isRequired ? ' *' : '');
    const value = values[f.fieldName] ?? '';
    if (f.fieldType === 'BOOLEAN') {
      return (
        <Box key={f.fieldName} sx={{ mb: 1 }}>
          <FormControlLabel
            control={<Checkbox checked={value === true} onChange={(e) => setValue(f, e.target.checked)} />}
            label={f.displayLabel}
          />
          {f.description && <FormHelperText sx={{ mt: -0.5, ml: 4 }}>{f.description}</FormHelperText>}
        </Box>
      );
    }
    if (f.fieldType === 'DROPDOWN' && f.allowedValues) {
      const opts = typeof f.allowedValues === 'string' ? JSON.parse(f.allowedValues) : f.allowedValues;
      const labelId = `inline-create-${f.fieldName}-label`;
      return (
        <FormControl key={f.fieldName} fullWidth size="small" sx={fieldSx}>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select labelId={labelId} label={label} value={value} onChange={(e) => setValue(f, e.target.value)}>
            {(Array.isArray(opts) ? opts : []).map((o) => {
              const v = typeof o === 'object' ? o.value : o;
              return <MenuItem key={v} value={v}>{typeof o === 'object' ? (o.label || v) : o}</MenuItem>;
            })}
          </Select>
          {f.description && <FormHelperText>{f.description}</FormHelperText>}
        </FormControl>
      );
    }
    return (
      <TextField
        key={f.fieldName}
        fullWidth size="small"
        type={f.fieldType === 'NUMBER' ? 'number' : (f.fieldType === 'EMAIL' ? 'email' : 'text')}
        label={label}
        placeholder={f.placeholder}
        value={value}
        onChange={(e) => setValue(f, e.target.value)}
        helperText={f.description}
        inputProps={f.maxLength ? { maxLength: f.maxLength } : undefined}
        sx={fieldSx}
      />
    );
  };

  const noun = option?.noun || 'record';
  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 0, bgcolor: colors.surface || colors.background } }}>
      <DialogTitle sx={{ color: colors.textPrimary }}>New {noun}</DialogTitle>
      <DialogContent {...enterAdvanceProps(() => { if (!saving) save(); })}>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
          {`Create the ${noun} here. When you save, it's selected on the form you were filling in, and nothing you entered there is lost.`}
        </Typography>
        {loading ? <CircularProgress size={24} /> : fields.map(renderInput)}
        {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || loading}
          sx={{ textTransform: 'none', borderRadius: 0, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
        >
          {saving ? 'Saving...' : `Create ${noun}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InlineCreateDialog;
